import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScopeComparison } from "@/components/ui/scope-comparison";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/hooks/use-toast";
import {
  ProjectReport,
  ProjectReportRes,
  ProjectService,
  ProjectStatus,
} from "@/services/project-service";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import IcInfo from "@/assets/icons/ic-info.svg";
import { usePermissions } from "@/lib/permissions";
import IcArrowNarrowLeft from "@/assets/icons/ic-arrow-narrow-left.svg";

const SECTIONS = [
  { key: "4. 보안 및 시스템 연계 요구사항" },
  { key: "5. 데이터 및 데이터 파이프라인 요건" },
  { key: "6. Agent 적합성 필터 (요약)" },
];

// Legacy fallback: old reports use "4. 보안 정책" instead of the new key
function getSection4Key(report: ProjectReport): string {
  if ("4. 보안 및 시스템 연계 요구사항" in report) {
    return "4. 보안 및 시스템 연계 요구사항";
  }
  if ("4. 보안 정책" in report) {
    return "4. 보안 정책";
  }
  return "4. 보안 및 시스템 연계 요구사항";
}

const STATUS_CONFIG: Record<
  ProjectStatus,
  { label: string; bg: string; text: string; dot: string }
> = {
  in_progress: {
    label: "진행 중",
    bg: "bg-[rgba(0,116,255,0.08)]",
    text: "text-[#003368]",
    dot: "bg-[#005ec2]",
  },
  under_review: {
    label: "검토중",
    bg: "bg-[rgba(255,186,0,0.12)]",
    text: "text-[#5c3d00]",
    dot: "bg-[#FFBA00]",
  },
  completed: {
    label: "완료",
    bg: "bg-[rgba(0,116,255,0.08)]",
    text: "text-[#003368]",
    dot: "bg-[#005ec2]",
  },
  deleted: {
    label: "삭제됨",
    bg: "bg-[rgba(0,0,0,0.04)]",
    text: "text-[#000000]",
    dot: "bg-[#8D8D8D]",
  },
};

export default function ProjectReportDetailPage() {
  const [searchParams] = useSearchParams();
  const { id } = useParams<{ id: string; mode?: string }>();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const canUpdate = can.update("project_management");
  const canComplete = can.create("project_management"); // "완료 처리" = status mutation, treat as admin-only

  const mode = searchParams.get("mode");
  const rawMode = mode === "edit" ? "edit" : "view";
  // Viewers/editors without update permission cannot enter edit mode.
  const currentMode = rawMode === "edit" && !canUpdate ? "view" : rawMode;

  const [report, setReport] = useState<ProjectReportRes | null>(null);
  const [editedReport, setEditedReport] = useState<ProjectReport | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);

  const fetchReport = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const response = await ProjectService.getProjectReport(id);
      if (response.data?.success) {
        setReport(response.data.data);
        setEditedReport(response.data.data.content);
      }
    } catch {
      toast({ description: "보고서를 불러오는데 실패했습니다." });
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const isAbleCompleted = useMemo(() => {
    return report && report.project_status === "under_review";
  }, [report]);

  const handleCancel = () => {
    setEditedReport(report?.content ?? null);
    navigate(`/projects/${id}`);
  };

  const handleSubmit = async () => {
    if (!id || !editedReport || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await ProjectService.editProjectReport({
        project_id: id,
        content: editedReport,
      });
      setReport((prev) => (prev ? { ...prev, content: editedReport } : null));
      navigate(`/projects/${id}`);
      toast({ description: "보고서가 저장되었습니다." });
    } catch {
      toast({
        description: "보고서 저장에 실패했습니다.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateSection = (sectionKey: string, label: string, value: string) => {
    if (!editedReport) return;
    const section = {
      ...(editedReport as unknown as Record<string, Record<string, unknown>>)[sectionKey],
    };
    section[label] = value;
    setEditedReport({ ...editedReport, [sectionKey]: section } as ProjectReport);
  };

  const updateNestedSection = (
    sectionKey: string,
    parentKey: string,
    childKey: string,
    value: string,
  ) => {
    if (!editedReport) return;
    const section = {
      ...(editedReport as unknown as Record<string, Record<string, unknown>>)[sectionKey],
    };
    const nested = { ...((section[parentKey] as Record<string, unknown>) || {}) };
    nested[childKey] = value;
    section[parentKey] = nested;
    setEditedReport({ ...editedReport, [sectionKey]: section } as ProjectReport);
  };

  const getActiveReport = () =>
    currentMode === "edit" && editedReport ? editedReport : (report?.content ?? null);
  const activeReport = getActiveReport();

  if (!report || !activeReport) {
    return null;
  }

  const section4Key = getSection4Key(activeReport);

  const handleCompleteReport = async () => {
    if (!id) return;
    try {
      setIsCompleting(true);
      await ProjectService.completeProject(id);
      // toast({ description: "프로젝트가 완료 처리되었습니다." });
      fetchReport();
    } catch (error) {
      // toast({ description: "완료 처리에 실패했습니다.", variant: "destructive" });
      console.log(error);
    } finally {
      setIsCompleting(false);
    }
  };

  return (
    <>
      {isLoading && (
        <div className="flex items-center justify-center py-20 h-[60vh] w-full">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-black/10 border-t-[#FF3B00]" />
        </div>
      )}
      <div
        className="flex flex-col gap-6 max-w-[896px] mx-auto"
        style={{ display: isLoading ? "none" : "flex" }}
      >
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/projects")}
            className="w-10 h-10 lg:w-12 lg:h-12"
          >
            <img src={IcArrowNarrowLeft} alt="Icon Arrow Left" />
          </Button>
          {currentMode === "view" && isAbleCompleted && (
            <Button
              onClick={handleCompleteReport}
              className="h-[48px] px-6  text-base bg-[#FFB07A] text-[#000000] font-bold"
              disabled={isCompleting}
            >
              완료 처리
            </Button>
          )}
        </div>
        <div className="flex flex-col gap-[24px] bg-white rounded">
          <div className="flex flex-col gap-6  pb-0 bg-[white] rounded">
            {/* Page Header Card */}
            <div className="bg-white rounded-xl">
              <div className="flex flex-col gap-3">
                <div className="flex items-start justify-between">
                  <div className="flex flex-col gap-1">
                    <h1 className="text-2xl font-bold text-[#000000]">
                      {report.project_name || "프로젝트명"}
                    </h1>
                    <span className="text-sm text-[#000000]">{activeReport["요약"]}</span>
                  </div>
                </div>

                <div className="h-px bg-[rgba(0,0,0,0.09)]" />
              </div>
            </div>

            {/* Edit Mode Banner */}
            {currentMode === "edit" && (
              <div className="flex items-center gap-4 p-4 bg-[#EBEBEB] rounded">
                <img src={IcInfo} />
                <p className="text-[#000] text-base">
                  편집 모드가 활성화되었습니다. 각 섹션을 클릭하여 수정할 수 있습니다.
                </p>
              </div>
            )}

            {/* Problem Situation */}
            <EditableSection
              title="문제 상황 정의"
              content={activeReport["문제 상황 정의"]}
              currentMode={currentMode}
              onChange={(value) =>
                setEditedReport((prev) => (prev ? { ...prev, "문제 상황 정의": value } : null))
              }
            />

            {/* 예산 범위 */}
            {activeReport["예산 범위"] && (
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-2">
                  <h2 className="text-xs text-[#B0B0B0]">예산 범위</h2>
                </div>
                <div
                  className="rounded p-5"
                  style={{
                    backgroundColor: "white",
                    border: "1px solid #00000017",
                  }}
                >
                  {Object.entries(activeReport["예산 범위"] || {}).map(([key, value], i, arr) => (
                    <div
                      key={key}
                      className={`flex justify-start items-center gap-10 py-4 ${
                        i < arr.length - 1 ? "border-b border-black/[0.09]" : ""
                      }`}
                    >
                      <p className="text-[#000000] font-bold min-w-[200px]">{key}</p>
                      {currentMode === "edit" ? (
                        <div className="flex-1 w-full">
                          <Input
                            value={
                              (editedReport?.["예산 범위"] as Record<string, string> | undefined)?.[
                                key
                              ] ?? (value as string) ?? ""
                            }
                            onChange={(e) => {
                              const newBudget = {
                                ...(editedReport?.["예산 범위"] || {}),
                              };
                              (newBudget as Record<string, string>)[key] = e.target.value;
                              setEditedReport((prev) =>
                                prev ? ({ ...prev, "예산 범위": newBudget } as any) : null,
                              );
                            }}
                            className="h-[44px] text-sm rounded w-full  font-normal"
                            placeholder="값을 입력하세요..."
                          />
                        </div>
                      ) : (
                        <p className="text-[#000000] text-sm">{(value as string) || "-"}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Section 1: 적용 업무 범위 */}
            <ScopeComparison
              title="1. 적용 업무 범위"
              inScopeItems={activeReport["1. 적용 업무 범위"]?.["In scope"] ?? []}
              isEditable={currentMode === "edit"}
              onInScopeChange={(items) => {
                const current = editedReport?.["1. 적용 업무 범위"] || {
                  "In scope": [],
                };
                setEditedReport((prev) =>
                  prev
                    ? {
                        ...prev,
                        "1. 적용 업무 범위": { ...current, "In scope": items },
                      }
                    : null,
                );
              }}
            />

            {/* Section 2: 도입 목적 */}
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-2">
                <h2 className="text-xs text-[#B0B0B0]">2. 도입 목적</h2>
              </div>
              <div
                className="w-full rounded p-5"
                style={{
                  backgroundColor: "white",
                  border: "1px solid #00000017",
                }}
              >
                {currentMode === "edit" ? (
                  <Textarea
                    value={(activeReport["2. 도입 목적"] as any)?.["도입 목적"] || ""}
                    onChange={(e) => {
                      setEditedReport((prev) =>
                        prev
                          ? ({ ...prev, "2. 도입 목적": { "도입 목적": e.target.value } } as any)
                          : null,
                      );
                    }}
                    placeholder="도입 목적을 입력하세요..."
                    className="w-full h-fit bg-transparent border-none   font-normal focus:ring-0 p-0 text-sm"
                  />
                ) : (
                  <p className="text-sm text-[#000000] whitespace-pre-wrap">
                    {(activeReport["2. 도입 목적"] as any)?.["도입 목적"] || "-"}
                  </p>
                )}
              </div>
            </div>

            {/* Section 3: 자동화 수준 정의 */}
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-2">
                <h2 className="text-xs text-[#B0B0B0]">3. 자동화 수준 정의</h2>
              </div>
              <div
                className="rounded p-5"
                style={{
                  backgroundColor: currentMode === "edit" ? "white" : "white",
                  border: "1px solid #00000017",
                }}
              >
                {/* 자동화 수준 (flat string field) */}
                <AutomationLevelRow
                  label="자동화 수준"
                  value={(activeReport["3. 자동화 수준 정의"] as any)?.["자동화 수준"] || ""}
                  mode={currentMode}
                  hasBorder
                  onChange={(val) => {
                    setEditedReport((prev) =>
                      prev
                        ? ({
                            ...prev,
                            "3. 자동화 수준 정의": {
                              ...((prev as any)["3. 자동화 수준 정의"] || {}),
                              "자동화 수준": val,
                            },
                          } as any)
                        : null,
                    );
                  }}
                />

                {/* 자동화 레벨 분류 및 세부 요건 (nested object field) */}
                {(activeReport["3. 자동화 수준 정의"] as any)?.[
                  "자동화 레벨 분류 및 세부 요건"
                ] && (
                  <>
                    <p className="text-[#000000] font-bold py-4">
                      자동화 레벨 분류 및 세부 요건
                    </p>
                    {Object.entries(
                      (activeReport["3. 자동화 수준 정의"] as any)[
                        "자동화 레벨 분류 및 세부 요건"
                      ] || {},
                    ).map(([subKey, subValue], i, arr) => (
                      <AutomationLevelRow
                        key={subKey}
                        label={`  └ ${subKey}`}
                        value={subValue as string}
                        mode={currentMode}
                        hasBorder={
                          i < arr.length - 1 ||
                          !!(activeReport["3. 자동화 수준 정의"] as any)?.["Human-in-the-loop"]
                        }
                        onChange={(val) => {
                          setEditedReport((prev) => {
                            if (!prev) return prev;
                            const section = { ...((prev as any)["3. 자동화 수준 정의"] || {}) };
                            const details = {
                              ...(section["자동화 레벨 분류 및 세부 요건"] || {}),
                            };
                            details[subKey] = val;
                            section["자동화 레벨 분류 및 세부 요건"] = details;
                            return { ...prev, "3. 자동화 수준 정의": section } as any;
                          });
                        }}
                      />
                    ))}
                  </>
                )}

                {/* Human-in-the-loop (flat string field) */}
                <AutomationLevelRow
                  label="Human-in-the-loop"
                  value={(activeReport["3. 자동화 수준 정의"] as any)?.["Human-in-the-loop"] || ""}
                  mode={currentMode}
                  hasBorder={false}
                  onChange={(val) => {
                    setEditedReport((prev) =>
                      prev
                        ? ({
                            ...prev,
                            "3. 자동화 수준 정의": {
                              ...((prev as any)["3. 자동화 수준 정의"] || {}),
                              "Human-in-the-loop": val,
                            },
                          } as any)
                        : null,
                    );
                  }}
                />
              </div>
            </div>

            {/* Sections 4-6 */}
            {SECTIONS.map(({ key }) => {
              // Section 4 may live under a legacy key ("4. 보안 정책") on old reports.
              const effectiveKey = key === "4. 보안 및 시스템 연계 요구사항" ? section4Key : key;
              return (
                <ReportSection
                  key={effectiveKey}
                  title={key}
                  items={
                    (activeReport as unknown as Record<string, Record<string, unknown>>)[
                      effectiveKey
                    ] || {}
                  }
                  currentMode={currentMode}
                  onUpdate={(label, value) => updateSection(effectiveKey, label, value)}
                  onUpdateNested={(parentKey, childKey, value) =>
                    updateNestedSection(effectiveKey, parentKey, childKey, value)
                  }
                />
              );
            })}
          </div>

          {currentMode === "edit" && (
            <div className="flex justify-center items-center gap-[8px] pb-[20px]">
              <Button
                onClick={handleCancel}
                variant={"ghost"}
                className="w-[175px]  font-bold border-none bg-[#EBEBEB] h-[48px]"
                disabled={isSubmitting}
              >
                취소
              </Button>
              <Button
                onClick={handleSubmit}
                className="w-[175px]  font-bold h-[48px] bg-primary"
                disabled={isSubmitting}
              >
                {"저장"}
              </Button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// Editable Section Component
interface EditableSectionProps {
  title: string;
  content: string;
  currentMode: "view" | "edit";
  onChange?: (value: string) => void;
}

function EditableSection({ title, content, currentMode, onChange }: EditableSectionProps) {
  return (
    <div className="flex flex-col gap-3">
      <h2 className="text-xs text-[#B0B0B0]">{title}</h2>
      <div
        className="w-full rounded p-5"
        style={{
          backgroundColor: "white",
          border: "1px solid #00000017",
        }}
      >
        {currentMode === "edit" ? (
          <Textarea
            value={content || ""}
            onChange={(e) => onChange?.(e.target.value)}
            placeholder="여기에 내용을 입력하세요..."
            className="w-full h-fit font-normal bg-transparent border-none focus:ring-0 p-0 text-sm"
          />
        ) : (
          <p className="text-sm text-[#000000] whitespace-pre-wrap">{content || "-"}</p>
        )}
      </div>
    </div>
  );
}

// Automation Level Row Component (renders a single flat key/value row for Section 3)
interface AutomationLevelRowProps {
  label: string;
  value: string;
  mode?: "view" | "edit";
  hasBorder: boolean;
  onChange?: (value: string) => void;
}

function AutomationLevelRow({ label, value, mode, hasBorder, onChange }: AutomationLevelRowProps) {
  return (
    <div
      className={`flex justify-start items-center gap-10 py-4 ${
        hasBorder ? "border-b border-black/[0.09]" : ""
      }`}
    >
      <p className="text-[#000000] font-bold min-w-[200px]">{label}</p>
      {mode === "edit" ? (
        <div className="flex-1 w-full">
          <Input
            value={value}
            onChange={(e) => onChange?.(e.target.value)}
            className="h-[44px] text-sm rounded w-full  font-normal"
            placeholder="값을 입력하세요..."
          />
        </div>
      ) : (
        <p className="text-[#000000] text-sm">{value || "-"}</p>
      )}
    </div>
  );
}

// Report Section Component (handles both flat string fields and nested
// dict-of-dict fields, e.g. "시스템 연계 요구사항" under section 4)
interface ReportSectionProps {
  title: string;
  // icon: React.ComponentType<{ className?: string }>;
  items: Record<string, unknown>;
  currentMode?: "view" | "edit";
  onUpdate?: (label: string, value: string) => void;
  onUpdateNested?: (parentKey: string, childKey: string, value: string) => void;
}

function ReportSection({
  title,
  // icon: Icon,
  items,
  currentMode,
  onUpdate,
  onUpdateNested,
}: ReportSectionProps) {
  const entries = Object.entries(items);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        {/* <Icon className="w-5 h-5 text-[#8D8D8D]" /> */}
        <h2 className="text-xs text-[#B0B0B0]">{title}</h2>
      </div>
      <div
        className="rounded p-5"
        style={{
          backgroundColor: "white",
          border: "1px solid #00000017",
        }}
      >
        <div className="flex flex-col gap-4">
          {entries.length > 0 ? (
            entries.map(([label, content]) => {
              // Nested dict field (e.g. "시스템 연계 요구사항")
              if (typeof content === "object" && content !== null && !Array.isArray(content)) {
                const nestedEntries = Object.entries(content as Record<string, string>);
                return (
                  <div key={label} className="flex flex-col gap-2">
                    <h3 className="font-bold text-[#000000] text-sm">{label}</h3>
                    <div className="rounded ml-2" style={{ border: "1px solid #00000017" }}>
                      {nestedEntries.map(([subLabel, subContent], si, sarr) => (
                        <div
                          key={subLabel}
                          className={`flex flex-col lg:flex-row ${
                            si < sarr.length - 1 ? "border-b border-black/[0.09]" : ""
                          }`}
                        >
                          <p className="text-[#000000] text-sm font-bold w-full lg:w-[200px] shrink-0 p-3 bg-[#FAFAFA]">
                            {subLabel}
                          </p>
                          <div className="flex-1 p-3">
                            {currentMode === "edit" ? (
                              <Textarea
                                value={subContent || ""}
                                onChange={(e) =>
                                  onUpdateNested?.(label, subLabel, e.target.value)
                                }
                                placeholder="여기에 내용을 입력하세요..."
                                className="w-full h-fit text-sm border rounded  font-normal"
                              />
                            ) : subContent ? (
                              <p className="text-sm text-[#000000] whitespace-pre-wrap">
                                {subContent}
                              </p>
                            ) : (
                              <p className="text-base text-[#BBBBBB]">-</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              }

              // Flat string field
              return (
                <div key={label} className="flex flex-col gap-2">
                  <h3 className="font-bold text-[#000000] text-sm">{label}</h3>
                  {currentMode === "edit" ? (
                    <div className="w-full">
                      <Textarea
                        value={(content as string) || ""}
                        onChange={(e) => onUpdate?.(label, e.target.value)}
                        placeholder="여기에 내용을 입력하세요..."
                        className="w-full h-fit text-sm border rounded  font-normal"
                      />
                    </div>
                  ) : content ? (
                    <p className="text-sm text-[#000000] whitespace-pre-wrap">
                      {content as string}
                    </p>
                  ) : (
                    <p className="text-base text-[#BBBBBB]">-</p>
                  )}
                </div>
              );
            })
          ) : (
            <p className="text-base text-[#BBBBBB]">-</p>
          )}
        </div>
      </div>
    </div>
  );
}
